// components/VariantGalaxy.tsx — Raw Three.js 3D protein landscape (mobile-friendly)
import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as THREE from 'three';
import { VariantData } from '../types';
import {
  fetchAlphaFoldStructure,
  getUniprotId,
  AlphaFoldStructure,
  calculateSpatialDistance,
} from '../services/alphafoldService';
import {
  fetchVariantLandscape,
  VariantLandscapeData,
  ClinVarVariant,
  FunctionalSite,
  isInFunctionalSite,
  calculatePathogenicityHeatmap,
} from '../services/variantLandscapeService';

// ============================================================
// Types
// ============================================================

interface VariantGalaxyProps {
  variantData: VariantData;
  className?: string;
}

type ViewMode = 'orbital' | 'constellation';

interface EnhancedResidue {
  position: number;
  aminoAcid: string;
  coords: THREE.Vector3;
  confidence: number;
  isExposed: boolean;
  variantType?: 'pathogenic' | 'likely_pathogenic' | 'benign' | 'likely_benign' | 'vus' | 'queried';
  clinvarData?: ClinVarVariant;
  isFunctionalSite: boolean;
  functionalSiteType?: string;
  pathogenicityScore: number;
}

interface GalaxyData {
  structure: AlphaFoldStructure | null;
  landscape: VariantLandscapeData | null;
  residues: EnhancedResidue[];
  heatmap: Map<number, number>;
  loading: boolean;
  error: string | null;
}

// ============================================================
// Helpers
// ============================================================

function classifySignificance(sig: string): EnhancedResidue['variantType'] {
  const s = sig.toLowerCase();
  if (s.includes('likely pathogenic') || s.includes('likely_pathogenic')) return 'likely_pathogenic';
  if (s.includes('pathogenic')) return 'pathogenic';
  if (s.includes('likely benign') || s.includes('likely_benign')) return 'likely_benign';
  if (s.includes('benign')) return 'benign';
  return 'vus';
}

function variantColor(type: string | undefined): THREE.Color {
  switch (type) {
    case 'pathogenic': return new THREE.Color(1, 0.15, 0.15);
    case 'likely_pathogenic': return new THREE.Color(1, 0.45, 0.15);
    case 'benign': return new THREE.Color(0.2, 0.6, 1);
    case 'likely_benign': return new THREE.Color(0.35, 0.75, 1);
    case 'vus': return new THREE.Color(0.5, 0.5, 0.5);
    case 'queried': return new THREE.Color(1, 1, 0);
    default: return new THREE.Color(0.3, 0.3, 0.3);
  }
}

function confidenceToColor(pLDDT: number): THREE.Color {
  if (pLDDT >= 90) return new THREE.Color(0, 0.33, 0.84);
  if (pLDDT >= 70) return new THREE.Color(0.4, 0.8, 0.95);
  if (pLDDT >= 50) return new THREE.Color(1, 0.86, 0.07);
  return new THREE.Color(1, 0.49, 0.27);
}

function heatmapColor(score: number): THREE.Color {
  if (score < 0.5) {
    return new THREE.Color(0, 0.4, 1).lerp(new THREE.Color(1, 1, 0), score * 2);
  }
  return new THREE.Color(1, 1, 0).lerp(new THREE.Color(1, 0, 0), (score - 0.5) * 2);
}

// ============================================================
// Three.js Scene
// ============================================================

class GalaxyScene {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  proteinGroup: THREE.Group;
  variantGroup: THREE.Group;
  constellationGroup: THREE.Group;
  raycaster: THREE.Raycaster;
  mouse: THREE.Vector2;
  clock: THREE.Clock;

  residues: EnhancedResidue[] = [];
  queriedPosition: number | null = null;
  viewMode: ViewMode = 'orbital';
  showHeatmap = false;
  hoveredMesh: THREE.Mesh | null = null;
  variantMeshes: Map<number, THREE.Mesh> = new Map();

  // Orbit controls (manual)
  private spherical = { theta: 0, phi: Math.PI / 3, radius: 60 };
  private targetLookAt = new THREE.Vector3();
  private isDragging = false;
  private lastPointerX = 0;
  private lastPointerY = 0;
  private lastPinchDist: number | null = null;

  animFrame = 0;
  disposed = false;

  onSelect: ((residue: EnhancedResidue | null) => void) | null = null;

  constructor(canvas: HTMLCanvasElement, width: number, height: number) {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
      powerPreference: 'default',
    });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setClearColor(0x0a0f1e, 1);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.2;

    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x0a0f1e, 0.003);

    this.camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);

    this.proteinGroup = new THREE.Group();
    this.variantGroup = new THREE.Group();
    this.constellationGroup = new THREE.Group();
    this.scene.add(this.proteinGroup);
    this.scene.add(this.variantGroup);
    this.scene.add(this.constellationGroup);

    // Lighting
    this.scene.add(new THREE.AmbientLight(0x404060, 0.6));
    const dir = new THREE.DirectionalLight(0xffffff, 1.0);
    dir.position.set(20, 30, 20);
    this.scene.add(dir);
    const back = new THREE.DirectionalLight(0x4a90e2, 0.4);
    back.position.set(-20, -10, -20);
    this.scene.add(back);
    const pt = new THREE.PointLight(0x4a90e2, 0.5, 100);
    pt.position.set(0, 20, 0);
    this.scene.add(pt);

    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2(-999, -999);
    this.clock = new THREE.Clock();

    this.updateCameraFromSpherical();
  }

  private updateCameraFromSpherical() {
    const { theta, phi, radius } = this.spherical;
    const x = this.targetLookAt.x + radius * Math.sin(phi) * Math.cos(theta);
    const y = this.targetLookAt.y + radius * Math.cos(phi);
    const z = this.targetLookAt.z + radius * Math.sin(phi) * Math.sin(theta);
    this.camera.position.set(x, y, z);
    this.camera.lookAt(this.targetLookAt);
  }

  // ---- Build protein ----
  buildProtein(residues: EnhancedResidue[], queriedPosition: number | null) {
    this.residues = residues;
    this.queriedPosition = queriedPosition;
    this.clearGroup(this.proteinGroup);
    this.clearGroup(this.variantGroup);
    this.variantMeshes.clear();

    if (residues.length === 0) return;

    // Center
    const center = new THREE.Vector3();
    residues.forEach(r => center.add(r.coords));
    center.divideScalar(residues.length);
    residues.forEach(r => r.coords.sub(center));
    this.targetLookAt.set(0, 0, 0);

    const queriedResidue = residues.find(r => r.position === queriedPosition);
    if (queriedResidue) {
      this.targetLookAt.copy(queriedResidue.coords);
    }

    this.buildBackbone(residues);
    this.buildVariantMarkers(residues, queriedPosition);
    this.buildFunctionalSites(residues);
    this.fitCamera(residues);
  }

  private buildBackbone(residues: EnhancedResidue[]) {
    const step = Math.max(1, Math.floor(residues.length / 500));
    const sphereGeo = new THREE.SphereGeometry(0.8, 8, 6);

    for (let i = 0; i < residues.length; i += step) {
      const r = residues[i];
      const color = this.showHeatmap ? heatmapColor(r.pathogenicityScore) : confidenceToColor(r.confidence);
      const mat = new THREE.MeshPhongMaterial({
        color, transparent: true, opacity: 0.35, depthWrite: false, side: THREE.DoubleSide,
      });
      const mesh = new THREE.Mesh(sphereGeo, mat);
      mesh.position.copy(r.coords);
      mesh.scale.setScalar(r.isExposed ? 1.2 : 0.8);
      this.proteinGroup.add(mesh);
    }

    // Backbone lines
    const positions: number[] = [];
    const colors: number[] = [];
    for (let i = 0; i < residues.length - 1; i++) {
      const a = residues[i].coords;
      const b = residues[i + 1].coords;
      positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
      const c = confidenceToColor(residues[i].confidence);
      colors.push(c.r, c.g, c.b, c.r, c.g, c.b);
    }
    const lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    lineGeo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    const lineMat = new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.6 });
    this.proteinGroup.add(new THREE.LineSegments(lineGeo, lineMat));
  }

  private buildVariantMarkers(residues: EnhancedResidue[], queriedPosition: number | null) {
    const variantResidues = residues.filter(r => r.variantType || r.position === queriedPosition);

    variantResidues.forEach(r => {
      const isQueried = r.position === queriedPosition;
      const type = isQueried ? 'queried' : r.variantType;
      const color = variantColor(type);

      let size = 0.5;
      if (isQueried) size = 1.8;
      else if (type === 'pathogenic') size = 1.2;
      else if (type === 'likely_pathogenic') size = 1.0;
      else if (type === 'benign' || type === 'likely_benign') size = 0.7;
      if (r.clinvarData) size *= (0.7 + r.clinvarData.starRating * 0.15);

      const geo = new THREE.SphereGeometry(size, isQueried ? 24 : 12, isQueried ? 16 : 8);
      const mat = new THREE.MeshPhongMaterial({
        color,
        emissive: color.clone().multiplyScalar(isQueried ? 0.8 : 0.3),
        transparent: !isQueried,
        opacity: isQueried ? 1.0 : 0.85,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.copy(r.coords);
      mesh.userData = { residue: r, isVariant: true };
      this.variantGroup.add(mesh);
      this.variantMeshes.set(r.position, mesh);

      // Glow for queried — multi-layer for visible bloom effect
      if (isQueried) {
        // Inner glow
        const glow1Geo = new THREE.SphereGeometry(size * 2, 16, 12);
        const glow1Mat = new THREE.MeshBasicMaterial({
          color: 0xffff00, transparent: true, opacity: 0.2, depthWrite: false,
        });
        const glow1 = new THREE.Mesh(glow1Geo, glow1Mat);
        glow1.position.copy(r.coords);
        glow1.userData = { isGlow: true, layer: 1 };
        this.variantGroup.add(glow1);

        // Outer glow
        const glow2Geo = new THREE.SphereGeometry(size * 4, 16, 12);
        const glow2Mat = new THREE.MeshBasicMaterial({
          color: 0xffee00, transparent: true, opacity: 0.08, depthWrite: false,
        });
        const glow2 = new THREE.Mesh(glow2Geo, glow2Mat);
        glow2.position.copy(r.coords);
        glow2.userData = { isGlow: true, layer: 2 };
        this.variantGroup.add(glow2);

        // Point light for actual illumination of nearby geometry
        const light = new THREE.PointLight(0xffff00, 2, 20);
        light.position.copy(r.coords);
        this.variantGroup.add(light);
        (light as any).userData = { isGlowLight: true };
      }

      // Ember for pathogenic
      if (type === 'pathogenic') {
        const emberGeo = new THREE.SphereGeometry(size * 1.8, 8, 6);
        const emberMat = new THREE.MeshBasicMaterial({
          color: 0xff2222, transparent: true, opacity: 0.08, depthWrite: false,
        });
        const ember = new THREE.Mesh(emberGeo, emberMat);
        ember.position.copy(r.coords);
        this.variantGroup.add(ember);
      }
    });
  }

  private buildFunctionalSites(residues: EnhancedResidue[]) {
    residues.filter(r => r.isFunctionalSite).forEach(r => {
      const geo = new THREE.SphereGeometry(1.5, 10, 8);
      const mat = new THREE.MeshPhongMaterial({
        color: 0xffd700,
        emissive: new THREE.Color(0xffd700).multiplyScalar(0.2),
        transparent: true, opacity: 0.3, depthWrite: false,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.copy(r.coords);
      mesh.userData = { isFunctionalSite: true, type: r.functionalSiteType };
      this.proteinGroup.add(mesh);
    });
  }

  // ---- Constellation ----
  buildConstellation(residues: EnhancedResidue[], queriedPosition: number | null) {
    this.clearGroup(this.constellationGroup);
    const vr = residues.filter(r => r.variantType || r.position === queriedPosition);
    if (vr.length === 0) return;

    vr.forEach(r => {
      const isQueried = r.position === queriedPosition;
      const type = isQueried ? 'queried' : r.variantType;
      const color = variantColor(type);
      const size = isQueried ? 1.5 : (r.pathogenicityScore > 0.7 ? 1.2 : 0.8);

      const geo = new THREE.SphereGeometry(size, 12, 8);
      const mat = new THREE.MeshPhongMaterial({
        color, emissive: color.clone().multiplyScalar(0.4), transparent: true, opacity: 0.9,
      });
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.copy(r.coords);
      mesh.userData = { residue: r, isVariant: true };
      this.constellationGroup.add(mesh);
    });

    const maxDist = 15;
    for (let i = 0; i < vr.length; i++) {
      for (let j = i + 1; j < vr.length; j++) {
        const dist = vr[i].coords.distanceTo(vr[j].coords);
        if (dist <= maxDist) {
          const aP = vr[i].pathogenicityScore > 0.5;
          const bP = vr[j].pathogenicityScore > 0.5;
          const isQ = vr[i].position === queriedPosition || vr[j].position === queriedPosition;
          let color = 0x444466, opacity = 0.15;
          if (isQ) { color = 0xffff00; opacity = 0.5; }
          else if (aP && bP) { color = 0xff4444; opacity = 0.4; }
          else if (aP || bP) { color = 0xff8844; opacity = 0.25; }

          const lineGeo = new THREE.BufferGeometry().setFromPoints([vr[i].coords, vr[j].coords]);
          const lineMat = new THREE.LineBasicMaterial({ color, transparent: true, opacity });
          this.constellationGroup.add(new THREE.Line(lineGeo, lineMat));
        }
      }
    }
  }

  // ---- Camera ----
  fitCamera(residues: EnhancedResidue[]) {
    let maxDist = 0;
    residues.forEach(r => {
      const d = r.coords.distanceTo(this.targetLookAt);
      if (d > maxDist) maxDist = d;
    });
    this.spherical.radius = maxDist * 1.8 + 20;
    this.updateCameraFromSpherical();
  }

  setViewMode(mode: ViewMode) {
    this.viewMode = mode;
    this.proteinGroup.visible = mode === 'orbital';
    this.variantGroup.visible = mode === 'orbital';
    this.constellationGroup.visible = mode === 'constellation';
    if (mode === 'constellation') this.buildConstellation(this.residues, this.queriedPosition);
  }

  setHeatmap(show: boolean) {
    this.showHeatmap = show;
    this.clearGroup(this.proteinGroup);
    this.buildBackbone(this.residues);
    this.buildFunctionalSites(this.residues);
  }

  // ---- Render ----
  animate() {
    if (this.disposed) return;
    this.animFrame = requestAnimationFrame(() => this.animate());
    const t = this.clock.getElapsedTime();

    // Animate queried glow
    this.variantGroup.children.forEach(child => {
      if (child.userData.isGlow) {
        const pulse = Math.sin(t * 2.5);
        if (child.userData.layer === 1) {
          child.scale.setScalar(1.0 + 0.25 * pulse);
          const mat = (child as THREE.Mesh).material as THREE.MeshBasicMaterial;
          mat.opacity = 0.15 + 0.1 * pulse;
        } else if (child.userData.layer === 2) {
          child.scale.setScalar(1.0 + 0.4 * pulse);
          const mat = (child as THREE.Mesh).material as THREE.MeshBasicMaterial;
          mat.opacity = 0.05 + 0.05 * pulse;
        }
      }
      if ((child as any).userData?.isGlowLight) {
        (child as THREE.PointLight).intensity = 1.5 + 1.0 * Math.sin(t * 2.5);
      }
      if (child.userData.isFunctionalSite) {
        child.scale.setScalar(0.9 + 0.15 * Math.sin(t * 2 + child.position.x));
      }
    });

    // Hover raycasting
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const group = this.viewMode === 'orbital' ? this.variantGroup : this.constellationGroup;
    const intersects = this.raycaster.intersectObjects(group.children, false);
    if (intersects.length > 0 && intersects[0].object.userData.residue) {
      const obj = intersects[0].object as THREE.Mesh;
      if (this.hoveredMesh !== obj) {
        if (this.hoveredMesh) this.hoveredMesh.scale.divideScalar(1.3);
        this.hoveredMesh = obj;
        obj.scale.multiplyScalar(1.3);
      }
    } else if (this.hoveredMesh) {
      this.hoveredMesh.scale.divideScalar(1.3);
      this.hoveredMesh = null;
    }

    this.renderer.render(this.scene, this.camera);
  }

  // ---- Interaction ----
  handlePointerMove(canvasX: number, canvasY: number, w: number, h: number) {
    this.mouse.x = (canvasX / w) * 2 - 1;
    this.mouse.y = -(canvasY / h) * 2 + 1;
  }

  handlePointerDown(x: number, y: number) {
    this.isDragging = true;
    this.lastPointerX = x;
    this.lastPointerY = y;
  }

  handlePointerDrag(x: number, y: number) {
    if (!this.isDragging) return;
    const dx = x - this.lastPointerX;
    const dy = y - this.lastPointerY;
    this.spherical.theta -= dx * 0.008;
    this.spherical.phi = Math.max(0.1, Math.min(Math.PI - 0.1, this.spherical.phi - dy * 0.008));
    this.lastPointerX = x;
    this.lastPointerY = y;
    this.updateCameraFromSpherical();
  }

  handlePointerUp() {
    this.isDragging = false;
  }

  handleZoom(delta: number) {
    this.spherical.radius = Math.max(10, Math.min(300, this.spherical.radius + delta));
    this.updateCameraFromSpherical();
  }

  handlePinchStart(dist: number) {
    this.lastPinchDist = dist;
  }

  handlePinchMove(dist: number) {
    if (this.lastPinchDist === null) return;
    const delta = (this.lastPinchDist - dist) * 0.3;
    this.handleZoom(delta);
    this.lastPinchDist = dist;
  }

  handlePinchEnd() {
    this.lastPinchDist = null;
  }

  handleClick(canvasX: number, canvasY: number, w: number, h: number) {
    this.mouse.x = (canvasX / w) * 2 - 1;
    this.mouse.y = -(canvasY / h) * 2 + 1;
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const group = this.viewMode === 'orbital' ? this.variantGroup : this.constellationGroup;
    const intersects = this.raycaster.intersectObjects(group.children, false);
    if (intersects.length > 0 && intersects[0].object.userData.residue) {
      this.onSelect?.(intersects[0].object.userData.residue as EnhancedResidue);
    } else {
      this.onSelect?.(null);
    }
  }

  resize(width: number, height: number) {
    this.renderer.setSize(width, height);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  private clearGroup(group: THREE.Group) {
    while (group.children.length > 0) {
      const child = group.children[0];
      group.remove(child);
      if ((child as THREE.Mesh).geometry) (child as THREE.Mesh).geometry.dispose();
      if ((child as THREE.Mesh).material) {
        const mat = (child as THREE.Mesh).material;
        if (Array.isArray(mat)) mat.forEach(m => m.dispose());
        else (mat as THREE.Material).dispose();
      }
    }
  }

  dispose() {
    this.disposed = true;
    cancelAnimationFrame(this.animFrame);
    this.clearGroup(this.proteinGroup);
    this.clearGroup(this.variantGroup);
    this.clearGroup(this.constellationGroup);
    this.renderer.dispose();
  }
}

// ============================================================
// React Component
// ============================================================

const VariantGalaxy: React.FC<VariantGalaxyProps> = ({ variantData, className = '' }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sceneRef = useRef<GalaxyScene | null>(null);
  const dragMovedRef = useRef(false);

  const [galaxyData, setGalaxyData] = useState<GalaxyData>({
    structure: null, landscape: null, residues: [], heatmap: new Map(),
    loading: true, error: null,
  });
  const [viewMode, setViewMode] = useState<ViewMode>('orbital');
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [tooltip, setTooltip] = useState<{
    x: number; y: number; residue: EnhancedResidue;
  } | null>(null);

  const queriedPosition = (() => {
    const match = variantData.proteinChange.match(/[A-Z](\d+)/);
    return match ? parseInt(match[1]) : null;
  })();

  // Load data
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setGalaxyData(prev => ({ ...prev, loading: true, error: null }));
        const uniprotId = (variantData.proteinId && !variantData.proteinId.startsWith('ENSP'))
          ? variantData.proteinId
          : getUniprotId(variantData.gene);
        if (!uniprotId) throw new Error(`No UniProt ID for ${variantData.gene}`);

        const structure = await fetchAlphaFoldStructure(uniprotId);
        const proteinLength = structure?.residues.length || 500;
        const landscape = await fetchVariantLandscape(variantData.gene, uniprotId, proteinLength);
        if (cancelled) return;
        if (!structure) throw new Error('Failed to load protein structure');

        const spatialPositions = structure.residues.map(r => ({
          position: r.number, coords: r.centerCoords as [number, number, number],
        }));
        const heatmap = landscape
          ? calculatePathogenicityHeatmap(landscape, spatialPositions)
          : new Map<number, number>();

        const residues: EnhancedResidue[] = structure.residues.map(r => {
          const clinvar = landscape?.clinvarVariants.find(v => v.position === r.number);
          const funcSite = landscape ? isInFunctionalSite(r.number, landscape.functionalSites) : null;
          return {
            position: r.number,
            aminoAcid: r.name,
            coords: new THREE.Vector3(...r.centerCoords),
            confidence: r.confidence,
            isExposed: r.isExposed,
            variantType: clinvar ? classifySignificance(clinvar.clinicalSignificance) : undefined,
            clinvarData: clinvar || undefined,
            isFunctionalSite: !!funcSite,
            functionalSiteType: funcSite?.type,
            pathogenicityScore: heatmap.get(r.number) || 0,
          };
        });

        setGalaxyData({ structure, landscape, residues, heatmap, loading: false, error: null });
      } catch (err) {
        if (!cancelled) {
          setGalaxyData(prev => ({
            ...prev, loading: false,
            error: err instanceof Error ? err.message : 'Failed to load',
          }));
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [variantData.gene, variantData.proteinId]);

  // Init Three.js
  useEffect(() => {
    if (galaxyData.loading || galaxyData.error || galaxyData.residues.length === 0) return;
    if (!canvasRef.current || !containerRef.current) return;

    const container = containerRef.current;
    const width = container.clientWidth;
    const height = Math.min(width * 0.85, 500);

    const galaxy = new GalaxyScene(canvasRef.current, width, height);
    sceneRef.current = galaxy;

    galaxy.onSelect = (residue) => {
      if (residue) {
        const pos = residue.coords.clone().project(galaxy.camera);
        const x = (pos.x * 0.5 + 0.5) * width;
        const y = (-pos.y * 0.5 + 0.5) * height;
        setTooltip({ x, y, residue });
      } else {
        setTooltip(null);
      }
    };

    galaxy.buildProtein(galaxyData.residues, queriedPosition);
    galaxy.animate();

    const ro = new ResizeObserver(() => {
      const w = container.clientWidth;
      const h = Math.min(w * 0.85, 500);
      galaxy.resize(w, h);
      canvasRef.current!.style.height = `${h}px`;
    });
    ro.observe(container);

    return () => { ro.disconnect(); galaxy.dispose(); sceneRef.current = null; };
  }, [galaxyData.loading, galaxyData.error, galaxyData.residues, queriedPosition]);

  // Sync state
  useEffect(() => { sceneRef.current?.setViewMode(viewMode); }, [viewMode]);
  useEffect(() => { sceneRef.current?.setHeatmap(showHeatmap); }, [showHeatmap]);

  // Mouse handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    dragMovedRef.current = false;
    sceneRef.current?.handlePointerDown(e.clientX, e.clientY);
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (rect) sceneRef.current?.handlePointerMove(e.clientX - rect.left, e.clientY - rect.top, rect.width, rect.height);
    if (e.buttons > 0) {
      dragMovedRef.current = true;
      sceneRef.current?.handlePointerDrag(e.clientX, e.clientY);
    }
  }, []);

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    sceneRef.current?.handlePointerUp();
    // Only fire click if we didn't drag
    if (!dragMovedRef.current) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) sceneRef.current?.handleClick(e.clientX - rect.left, e.clientY - rect.top, rect.width, rect.height);
    }
  }, []);

  // Wheel zoom — must use native listener to set { passive: false }
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      sceneRef.current?.handleZoom(e.deltaY * 0.15);
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [galaxyData.loading]);

  // Touch handlers
  const touchStateRef = useRef<{ startX: number; startY: number; moved: boolean }>({ startX: 0, startY: 0, moved: false });

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      sceneRef.current?.handlePointerDown(e.touches[0].clientX, e.touches[0].clientY);
      touchStateRef.current = { startX: e.touches[0].clientX, startY: e.touches[0].clientY, moved: false };
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      sceneRef.current?.handlePinchStart(Math.sqrt(dx * dx + dy * dy));
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    if (e.touches.length === 1) {
      touchStateRef.current.moved = true;
      sceneRef.current?.handlePointerDrag(e.touches[0].clientX, e.touches[0].clientY);
    } else if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      sceneRef.current?.handlePinchMove(Math.sqrt(dx * dx + dy * dy));
    }
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    sceneRef.current?.handlePointerUp();
    sceneRef.current?.handlePinchEnd();
    // Tap detection
    if (e.changedTouches.length === 1 && !touchStateRef.current.moved) {
      const rect = canvasRef.current?.getBoundingClientRect();
      if (rect) {
        sceneRef.current?.handleClick(
          e.changedTouches[0].clientX - rect.left,
          e.changedTouches[0].clientY - rect.top,
          rect.width, rect.height,
        );
      }
    }
  }, []);

  if (galaxyData.loading) {
    return (
      <div className={`relative w-full rounded-lg overflow-hidden ${className}`}
           style={{ height: '400px', background: 'linear-gradient(180deg, #0a0f1e 0%, #060a14 100%)' }}>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full mx-auto mb-3"></div>
            <div className="text-white text-sm">Loading protein structure...</div>
            <div className="text-slate-400 text-xs mt-1">Fetching AlphaFold + ClinVar data</div>
          </div>
        </div>
      </div>
    );
  }

  if (galaxyData.error) {
    return (
      <div className={`relative w-full rounded-lg overflow-hidden ${className}`}
           style={{ height: '400px', background: 'linear-gradient(180deg, #0a0f1e 0%, #060a14 100%)' }}>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-6">
            <div className="text-red-400 text-2xl mb-2">⚠️</div>
            <div className="text-white text-sm">Failed to load structure</div>
            <div className="text-slate-400 text-xs mt-1">{galaxyData.error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative w-full rounded-lg overflow-hidden select-none ${className}`}>
      <canvas
        ref={canvasRef}
        className="w-full cursor-grab active:cursor-grabbing"
        style={{ touchAction: 'none', height: '400px' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => sceneRef.current?.handlePointerUp()}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      />

      {/* Tooltip */}
      {tooltip && (
        <div className="absolute pointer-events-none z-10"
             style={{ left: Math.min(tooltip.x, (containerRef.current?.clientWidth || 300) - 200), top: Math.max(tooltip.y - 90, 10) }}>
          <div className="bg-black/90 backdrop-blur-sm text-white px-3 py-2 rounded-lg shadow-xl text-xs max-w-[220px]">
            <div className="font-semibold text-sm">
              {tooltip.residue.aminoAcid}{tooltip.residue.position}
            </div>
            {tooltip.residue.variantType && (
              <div style={{ color: `#${variantColor(tooltip.residue.variantType).getHexString()}` }}>
                {tooltip.residue.clinvarData?.clinicalSignificance || tooltip.residue.variantType.replace('_', ' ')}
              </div>
            )}
            {tooltip.residue.clinvarData && (
              <div className="text-slate-300">
                {'⭐'.repeat(tooltip.residue.clinvarData.starRating)} · {tooltip.residue.clinvarData.conditions[0]}
              </div>
            )}
            {tooltip.residue.isFunctionalSite && (
              <div className="text-yellow-400">🔶 {tooltip.residue.functionalSiteType}</div>
            )}
            <div className="text-slate-400 mt-1 border-t border-slate-600 pt-1">
              <div>Confidence: {tooltip.residue.confidence.toFixed(1)}%</div>
              <div>Surface: {tooltip.residue.isExposed ? 'Exposed' : 'Buried'}</div>
              <div>Risk: {(tooltip.residue.pathogenicityScore * 100).toFixed(0)}%</div>
            </div>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-8 pb-3 px-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-white/10 rounded-lg overflow-hidden">
            <button onClick={() => setViewMode('orbital')}
              className={`text-xs px-3 py-1.5 transition-colors ${viewMode === 'orbital' ? 'bg-blue-500 text-white' : 'text-slate-300 hover:bg-white/10'}`}>
              🌍 Orbital
            </button>
            <button onClick={() => setViewMode('constellation')}
              className={`text-xs px-3 py-1.5 transition-colors ${viewMode === 'constellation' ? 'bg-blue-500 text-white' : 'text-slate-300 hover:bg-white/10'}`}>
              ✨ Constellation
            </button>
          </div>
          <button onClick={() => setShowHeatmap(!showHeatmap)}
            className={`text-xs px-2.5 py-1.5 rounded-lg transition-colors ${showHeatmap ? 'bg-red-500/80 text-white' : 'bg-white/10 text-slate-300 hover:bg-white/20'}`}>
            {showHeatmap ? '🔥 Danger' : '🧬 pLDDT'}
          </button>
        </div>
      </div>

      {/* Legend */}
      <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2 text-xs text-slate-300 space-y-1">
        <div className="flex items-center gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-yellow-400 inline-block" style={{ boxShadow: '0 0 6px #fde047' }}></span>
          <span>Queried</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span>
          <span>Pathogenic</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-blue-400 inline-block"></span>
          <span>Benign</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-gray-500 inline-block"></span>
          <span>VUS</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-yellow-600 inline-block"></span>
          <span>Functional</span>
        </div>
      </div>

      {/* Info */}
      <div className="absolute top-3 left-3 bg-black/60 backdrop-blur-sm rounded-lg px-3 py-2">
        <div className="text-white text-sm font-semibold">{variantData.gene}</div>
        <div className="text-slate-300 text-xs">{variantData.proteinChange}</div>
        <div className="text-slate-400 text-xs">
          {galaxyData.residues.length} residues · {galaxyData.landscape?.clinvarVariants.length || 0} variants
        </div>
      </div>
    </div>
  );
};

export default VariantGalaxy;
